import React, { useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import {
  AccordionSection,
  Button,
  Checkbox,
  ChipsField,
  ChoiceField,
  DateField,
  DropdownField,
  FormSection,
  MeasureField,
  MultiSelectField,
  PinnedActionBar,
  TextField,
  TimeRangeField,
  type DropdownOption,
  type ProblemReport,
  useProblemReport,
  useToast,
} from "../../../components/ui";
import { useHiddenTabBar } from "../../../components/layout/tabBarVisibility";
import {
  HABIT_OPTIONS,
  MAX_DISHES,
} from "../../preconsultation/preconsultationSteps";
import {
  normaliseEmailInput,
  normalisePhoneInput,
  normalisePincodeInput,
  toNameCase,
} from "../../../lib/fieldFormat";
import { STATE_OPTIONS, citiesOfState } from "../../../lib/locations";
import { formatPrice } from "../../brand/planFormat";
import type { PlansStatus } from "../../brand/hooks/usePlans";
import type { BrandPlan } from "../../brand/types/brand.types";
import { GENDER_OPTIONS, PROFESSION_OPTIONS } from "../data/clientFormOptions";
import {
  LOOKUP_NAMES,
  type ClientLookup,
  type ClientLookups,
} from "../hooks/useClientLookups";
import type { HeightUnit, WeightUnit } from "../types/clients.types";
import { NewClient } from "../types/clients.types";
import {
  MAX_AGE_YEARS,
  NewClientErrors,
  ValidatedField,
  normaliseAmountInput,
  normaliseDobInput,
  normaliseNameInput,
} from "../validation";

/**
 * The validated fields in the order they are laid out, which is the order the
 * first problem has to be looked for in — `VALIDATED_FIELDS` is grouped by
 * required-ness instead, and would send you to the wrong one.
 */
const FIELD_ORDER: ValidatedField[] = [
  "title",
  "firstName",
  "lastName",
  "gender",
  "dob",
  "profession",
  "lifestyle",
  "state",
  "city",
  "pincode",
  "phone",
  "whatsapp",
  "email",
  "goals",
  "foodPreference",
  "foodAllergies",
  "planId",
  "paidPrice",
  "purchasedDate",
];

/** Stable id per control, so a failing field can be reached and focused. */
const controlId = (field: ValidatedField) => `client-${field}`;

/**
 * The same id scheme for the optional questions, which have no validation
 * behind them and so are not `ValidatedField`s — nothing ever scrolls to one,
 * but a control still needs an id for the label above it to point at.
 */
const otherId = (field: keyof NewClient) => `client-${field}`;

const HEIGHT_UNITS: readonly HeightUnit[] = ["cm", "in"];
const WEIGHT_UNITS: readonly WeightUnit[] = ["kg", "lb"];

/** The two answers to "On any medications", with the design's tick and cross. */
const MEDICATION_CHOICES = [
  { value: "No" as const, icon: <X size={18} aria-hidden="true" /> },
  { value: "Yes" as const, icon: <Check size={18} aria-hidden="true" /> },
];

/** A group of optional questions inside the accordion — the form's own steps. */
const OtherGroup: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section>
    <h3 className="mb-3 text-[13px] 3xl:text-[14px] font-medium text-ink">
      {title}
    </h3>
    {children}
  </section>
);

/**
 * How many of the optional questions have been answered.
 *
 * Only for the count beside the accordion's title, so a section that was
 * filled in and folded back up still says so. Counted rather than stored: the
 * answers live on the client like every other field, and a second flag saying
 * whether they are there is a second thing to keep true.
 */
const otherAnswerCount = (client: NewClient): number =>
  [
    client.height,
    client.weight,
    client.targetWeight,
    client.onMedication,
    client.travel,
    client.alcohol,
    client.smoking,
  ].filter((value) => value.trim()).length +
  [
    client.cuisines,
    client.dishesLoved,
    client.dishesAvoided,
    client.conditions,
    client.medications,
  ].filter((list) => list.length > 0).length +
  // A pair of hours is one answer, and so is having said there are none.
  [
    client.workAnyHours || (client.workFrom && client.workTo),
    client.sleepAnyHours || (client.sleepFrom && client.sleepTo),
  ].filter(Boolean).length;

/**
 * What the DOB calendar will offer, matching the two rules `fieldError` checks
 * for the same field: not in the future, and not more than `MAX_AGE_YEARS`
 * back. Read once at module load — the boundary moving at midnight matters to
 * nobody entering a date of birth.
 */
const TODAY = new Date();
const EARLIEST_DOB = new Date(
  TODAY.getFullYear() - MAX_AGE_YEARS,
  0,
  1
);

/**
 * The workspace's plans, for the Subscription details section — handed in
 * rather than fetched here, because the page needs the same list to price what
 * was typed into Paid price against the plan it was picked for.
 */
/** One array for every render without a catalog — see `plans` below. */
const NO_PLANS: BrandPlan[] = [];

/** What the Plan field is handed, once its list state has been read. */
interface PlanFieldProps {
  options: DropdownOption[];
  placeholder: string;
  searchable?: boolean;
  disabled?: boolean;
  onDisabledClick?: () => void;
}

export interface SubscriptionCatalog {
  /** Already filtered to what can be sold — see `AddClientPage`. */
  plans: BrandPlan[];
  status: PlansStatus;
}

interface AddClientFormProps {
  client: NewClient;
  /** Patch merge — every control sends only the key it owns. */
  onChange: (patch: Partial<NewClient>) => void;
  /** Failing fields, keyed by field name. */
  errors: NewClientErrors;
  /**
   * The five lists the backend owns — titles, goals, dietary patterns,
   * allergens and life styles — plus the `reload` a failed field offers.
   */
  lookups: ClientLookups;
  /**
   * Given, the form asks for the subscription the client arrives already
   * holding; left off, it does not — which is how the edit screen opens, since
   * an edit is not a purchase and the Payments tab is where later ones are
   * raised.
   */
  subscription?: SubscriptionCatalog;
  /** The save is in flight — the action bar says so and refuses a second one. */
  saving?: boolean;
  /**
   * What the submit button says. The edit screen is this same form with the
   * answers already in it, so the label is the only thing that has to tell the
   * two apart — "Save & add" makes a promise an edit does not keep.
   */
  saveLabel?: string;
  onSave: () => void;
  onCancel: () => void;
  /**
   * Filled with this form's own way of pointing at what is missing, so the
   * page above can ask for it — the rail's unsaved prompt does. See
   * `useProblemReport`.
   */
  problems?: ProblemReport;
}

const AddClientForm: React.FC<AddClientFormProps> = ({
  client,
  onChange,
  errors,
  lookups,
  subscription,
  saving = false,
  saveLabel = "Save & add",
  onSave,
  onCancel,
  problems,
}) => {
  const toast = useToast();

  // This form's own Save and Cancel are the bar at the foot of a phone, so the
  // shell's tab bar stands down under it — two bars stacked there is one too
  // many, and the lower of them offers to leave the form by a door that throws
  // the answers away. The same claim `BrandDetailsForm` and `ProfileForm`
  // make, and for the same reason: adding or editing a client is a job rather
  // than a screen. The rail is still a press away behind the avatar in the top
  // bar, and the unsaved-changes guard still asks about the form before it
  // goes. See `tabBarVisibility`.
  useHiddenTabBar();

  // A field's error only surfaces once it has been visited, so the form does
  // not open covered in red before anything has been typed.
  const [touched, setTouched] = useState<
    Partial<Record<ValidatedField, boolean>>
  >({});

  const visit = (field: ValidatedField) => () =>
    setTouched((current) => ({ ...current, [field]: true }));

  const errorFor = (field: ValidatedField) =>
    touched[field] ? errors[field] : undefined;

  const valid = Object.keys(errors).length === 0;

  // While the numbers are linked, editing the phone has to carry the WhatsApp
  // number with it — otherwise the two drift apart behind a ticked box.
  const handlePhoneChange = (raw: string) => {
    const phone = normalisePhoneInput(raw);
    onChange(client.whatsappSameAsPhone ? { phone, whatsapp: phone } : { phone });
  };

  // Unticking leaves the copied number in place rather than blanking it: it is
  // almost always the right starting point for the edit that follows.
  const handleSameAsPhone = (checked: boolean) =>
    onChange(
      checked
        ? { whatsappSameAsPhone: true, whatsapp: client.phone }
        : { whatsappSameAsPhone: false }
    );

  // The city list belongs to the state, so changing the state drops whatever
  // city was picked under the old one — keeping it would leave the address
  // naming a city that is not in the state beside it.
  const handleStateChange = (state: string) => onChange({ state, city: "" });

  // The same empty array every render where there is no catalog, so the list
  // below is not rebuilt for a section that is not even on screen.
  const plans = subscription?.plans ?? NO_PLANS;

  /**
   * The plans, read exactly as the Create new payment dialog reads them:
   * name and duration together, because a workspace sells the same plan at
   * several lengths and the name alone picks none of them — with the price
   * under it, since that is what the Paid price beside it is measured against.
   *
   * The value is the plan's `public_id`, which is what the endpoint takes,
   * so nothing has to be matched back from the label on the way out.
   */
  const planOptions: DropdownOption[] = useMemo(
    () =>
      plans.map((plan) => ({
        value: plan.id,
        label: `${plan.name} | ${plan.duration}`,
        subtitle: formatPrice(plan.price),
      })),
    [plans]
  );

  /**
   * Picking a plan prices the request from it, the way the payment dialog
   * does: the list price lands in Paid price ready to be lowered where a
   * discount was given, rather than leaving the box empty for a figure that is
   * nearly always the one just picked.
   */
  const handlePlanChange = (planId: string) =>
    onChange({
      planId,
      paidPrice: String(
        plans.find((plan) => plan.id === planId)?.price ?? ""
      ),
    });

  /**
   * The Plan field's own list state, told the same way the catalog fields
   * above are told theirs: shut while there is nothing to pick, and reachable
   * enough to say why. A workspace that has listed no active plan is its
   * own case — nothing failed, there is simply nothing to sell yet, and the
   * way out of it is the Plans tab rather than a retry.
   */
  const planField = ((): PlanFieldProps => {
    const status = subscription?.status ?? "loading";
    const placeholder =
      status === "loading" ? "Loading…" : "eg. Weight loss | 2 months";

    if (status === "ready" && planOptions.length > 0) {
      return {
        options: planOptions,
        placeholder,
        // Long lists are typed into rather than scrolled, matching the payment
        // dialog's own threshold.
        searchable: planOptions.length > 8,
      };
    }

    return {
      options: planOptions,
      placeholder,
      disabled: true,
      onDisabledClick: () => {
        if (status === "loading") {
          toast.info("Still loading", "Your plans are on their way.");
          return;
        }
        if (status === "failed") {
          toast.warning(
            "Could not load your plans",
            "Reload the page to try again."
          );
          return;
        }
        toast.info(
          "No plans to sell yet",
          "Add an active plan under My brand to record a subscription."
        );
      },
    };
  })();

  /**
   * Wires a dropdown to one of the lists the backend owns — every field in
   * this form bar Gender, Profession, State and City.
   *
   * A list that has not arrived leaves its field shut, but reachable rather
   * than dead: reaching for it says why it is empty. It does not refetch —
   * the lists are loaded once when the page opens, and a field that asked for
   * them again on every click would put five requests on the wire each time
   * someone opened it. Recovering from a failure is the Try again button
   * above, which is pressed on purpose and only once.
   *
   * An empty list that came back fine gets its own message rather than the
   * failure's: nothing went wrong, there is simply nothing to pick yet.
   */
  const lookupField = (lookup: ClientLookup, name: string, sample: string) => {
    const { status, names } = lookup;
    const placeholder = status === "loading" ? "Loading…" : `eg. ${sample}`;

    if (status === "ready" && names.length > 0) {
      return { options: names, placeholder };
    }

    return {
      options: names,
      placeholder,
      disabled: true,
      onDisabledClick: () => {
        if (status === "loading") {
          toast.info("Still loading", `The ${name} list is on its way.`);
          return;
        }
        if (status === "failed") {
          toast.warning(
            `Could not load the ${name} list`,
            "Use Try again at the top of the form."
          );
          return;
        }
        toast.info(
          `No ${name} options yet`,
          `Nothing has been set up to pick from.`
        );
      },
    };
  };

  // Every list is loaded by one pass when the page opens, so they fail
  // together far more often than singly — one banner for the form rather than
  // a retry hidden behind each field that happens to be empty.
  const failed = LOOKUP_NAMES.filter(
    (name) => lookups[name].status === "failed"
  );

  /**
   * Points at the problem rather than describing it: every failing field
   * reports itself at once, and the page moves to the first of them and puts
   * the cursor in it.
   */
  const reportProblems = () => {
    const firstBad = FIELD_ORDER.find((field) => errors[field]);
    if (!firstBad) return;

    setTouched(Object.fromEntries(FIELD_ORDER.map((field) => [field, true])));

    // Focus without its own jump, so the smooth scroll is the only movement
    // and the field lands in the middle of the column rather than its edge.
    const control = document.getElementById(controlId(firstBad));
    control?.focus({ preventScroll: true });
    control?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  // Also reachable from the page above, for the one press that is not made on
  // this form: the rail's unsaved prompt offering to save it.
  useProblemReport(problems, reportProblems);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Enter in a field reaches this even while the button is off, so the
    // incomplete form is marked up here too rather than silently ignored.
    if (!valid) {
      reportProblems();
      return;
    }

    onSave();
  };

  return (
    <form className="mt-5 flex flex-1 flex-col" onSubmit={handleSubmit} noValidate>
      {/* The sections have no scroller of their own: the page is what scrolls,
          under the shell's scrollbar on the far right. `flex-1` is what takes
          up the slack on a short viewport, so the action bar below is pushed
          to the foot of the screen rather than floating under the last card. */}
      <div className="flex flex-1 flex-col gap-5 pb-5">
        {/* The one place the form asks for its lists again. Shown only when
            something failed, so the usual path never sees it — and pressed on
            purpose, which is what keeps the five calls off every click. */}
        {failed.length > 0 && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#F5C2C2] bg-[#FDECEC] px-4 py-3"
          >
            <p className="text-[13px] 3xl:text-[14px] text-[#8A2B2B]">
              {failed.length === LOOKUP_NAMES.length
                ? "The form's options could not be loaded, so some fields have nothing to pick from."
                : "Some of the form's options could not be loaded, so those fields have nothing to pick from."}
            </p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={lookups.reload}
            >
              Try again
            </Button>
          </div>
        )}

        <FormSection title="Basic details">
          {/* Title is the narrow one of the three: it holds "Mr", where the
              names beside it hold whatever the client is actually called. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1.4fr)]">
            <DropdownField
              label="Title"
              id={controlId("title")}
              {...lookupField(lookups.titles, "title", "Ms")}
              value={client.title}
              error={errorFor("title")}
              onBlur={visit("title")}
              onValueChange={(title) => onChange({ title })}
            />
            {/* Cased on the way in rather than on save, so the list and the
                detail screen read the name back exactly as the field holds
                it — "john" and "JOHN" both arrive as "John". */}
            <TextField
              label="First name"
              id={controlId("firstName")}
              placeholder="eg. Riya"
              autoComplete="given-name"
              required
              value={client.firstName}
              error={errorFor("firstName")}
              onBlur={visit("firstName")}
              onValueChange={(firstName) =>
                onChange({ firstName: toNameCase(normaliseNameInput(firstName)) })
              }
            />
            <TextField
              label="Last name"
              id={controlId("lastName")}
              placeholder="eg. Roy"
              autoComplete="family-name"
              required
              value={client.lastName}
              error={errorFor("lastName")}
              onBlur={visit("lastName")}
              onValueChange={(lastName) =>
                onChange({ lastName: toNameCase(normaliseNameInput(lastName)) })
              }
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-4">
            <DropdownField
              label="Gender"
              id={controlId("gender")}
              placeholder="eg. Male"
              options={GENDER_OPTIONS}
              required
              value={client.gender}
              error={errorFor("gender")}
              onBlur={visit("gender")}
              onValueChange={(gender) => onChange({ gender })}
            />
            {/* Both ways in: typed, where the normaliser puts the slashes in
                as you go, or picked from the calendar the field opens. A date
                of birth is decades back, which is why the calendar's header
                drills down to a page of years rather than only paging months.

                The bounds are the ones `validateNewClient` already enforces,
                so the calendar cannot offer a day the form would then refuse. */}
            <DateField
              label="DOB"
              id={controlId("dob")}
              value={client.dob}
              normalise={normaliseDobInput}
              minDate={EARLIEST_DOB}
              maxDate={TODAY}
              error={errorFor("dob")}
              onBlur={visit("dob")}
              onValueChange={(dob) => onChange({ dob })}
            />
            <DropdownField
              label="Profession"
              id={controlId("profession")}
              placeholder="eg. Working"
              options={PROFESSION_OPTIONS}
              value={client.profession}
              error={errorFor("profession")}
              onBlur={visit("profession")}
              onValueChange={(profession) => onChange({ profession })}
            />
            <DropdownField
              label="Life style"
              id={controlId("lifestyle")}
              {...lookupField(lookups.lifeStyles, "life style", "Semi active")}
              required
              value={client.lifestyle}
              error={errorFor("lifestyle")}
              onBlur={visit("lifestyle")}
              onValueChange={(lifestyle) => onChange({ lifestyle })}
            />
          </div>
        </FormSection>

        <FormSection title="Communication details">
          {/* The street address gets the full width of the card to itself: it
              is the one line here long enough to need it. */}
          <TextField
            label="Full address"
            placeholder="1202, Galaxy apartment, SV Road"
            autoComplete="street-address"
            value={client.address}
            onValueChange={(address) => onChange({ address })}
          />

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
            {/* State leads City, because it is what fills it. Laid out the
                other way round the reader meets a dead control before the one
                that brings it to life.

                Both lists are typed into rather than scrolled: 34 states and
                up to 120 cities are more than anyone wants to hunt through. */}
            <DropdownField
              label="State"
              id={controlId("state")}
              placeholder="eg. Maharashtra"
              options={STATE_OPTIONS}
              searchable
              value={client.state}
              error={errorFor("state")}
              onBlur={visit("state")}
              onValueChange={handleStateChange}
            />
            <DropdownField
              label="City"
              id={controlId("city")}
              placeholder={client.state ? "eg. Mumbai" : "Select a state first"}
              options={citiesOfState(client.state)}
              disabled={!client.state}
              // Reaching for the city before there is a state to fill it is a
              // reasonable thing to do, so it gets an answer rather than a
              // control that simply refuses to respond.
              onDisabledClick={() =>
                toast.warning(
                  "Select a state first",
                  "The city list is filled by the state you pick."
                )
              }
              searchable
              // The list covers the common cases and cannot claim to be
              // exhaustive: a town it has never heard of is still where a
              // client lives. Typing one offers it as a "Use …" row under
              // whatever matched, and a value already saved that way shows in
              // the closed control rather than reading as blank.
              creatable
              required
              value={client.city}
              error={errorFor("city")}
              onBlur={visit("city")}
              onValueChange={(city) => onChange({ city })}
            />
            <TextField
              label="Pincode"
              id={controlId("pincode")}
              placeholder="400130"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={6}
              value={client.pincode}
              error={errorFor("pincode")}
              onBlur={visit("pincode")}
              onValueChange={(pincode) =>
                onChange({ pincode: normalisePincodeInput(pincode) })
              }
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
            <TextField
              label="Phone number"
              id={controlId("phone")}
              placeholder=" 7869 369 854"
              // The field holds the ten subscriber digits; the dialling code is
              // drawn beside them and put back on for the API.
              prefix="+91"
              inputMode="numeric"
              autoComplete="tel"
              // The normaliser already stops at ten digits; `maxLength` is
              // what makes the browser say so before the keystroke lands.
              maxLength={10}
              required
              value={client.phone}
              error={errorFor("phone")}
              onBlur={visit("phone")}
              onValueChange={handlePhoneChange}
            />
            <div>
              <TextField
                label="WhatsApp number"
                id={controlId("whatsapp")}
                placeholder=" 7869 369 854"
                prefix="+91"
                inputMode="numeric"
                maxLength={10}
                // Mirrored fields are read-only: the phone beside it is the
                // one place the number is edited while the box is ticked.
                disabled={client.whatsappSameAsPhone}
                required
                value={client.whatsapp}
                error={errorFor("whatsapp")}
                onBlur={visit("whatsapp")}
                onValueChange={(whatsapp) =>
                  onChange({ whatsapp: normalisePhoneInput(whatsapp) })
                }
              />
              <Checkbox
                label="Same as phone number"
                className="mt-2"
                checked={client.whatsappSameAsPhone}
                onCheckedChange={handleSameAsPhone}
              />
            </div>
            <TextField
              label="Email address"
              id={controlId("email")}
              type="email"
              placeholder="eg. riyaroy@gmail.com"
              autoComplete="email"
              required
              value={client.email}
              error={errorFor("email")}
              onBlur={visit("email")}
              onValueChange={(email) =>
                onChange({ email: normaliseEmailInput(email) })
              }
            />
          </div>
        </FormSection>

        <FormSection title="Requirements">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
            {/* Several at once, the way the allergies beside it are: a client
                may be signed up for more than one thing, and the record carries
                them as a list. Unlike the allergies, at least one is required —
                a client with no goal is not somebody anybody is writing a plan
                for.

                The list is the practice's own active services rather than the
                backend's goal catalog — what a client is signed up against is
                something this practitioner offers. The field is still Goal:
                that is what it is being asked, and the services are named for
                the goals they serve. */}
            <MultiSelectField
              label="Goal"
              id={controlId("goals")}
              {...lookupField(lookups.serviceGoals, "goal", "Weight loss")}
              required
              values={client.goals}
              error={errorFor("goals")}
              onBlur={visit("goals")}
              onValuesChange={(goals) => onChange({ goals })}
            />
            <DropdownField
              label="Food preference"
              id={controlId("foodPreference")}
              {...lookupField(lookups.dietaryPatterns, "food preference", "Veg")}
              value={client.foodPreference}
              error={errorFor("foodPreference")}
              onBlur={visit("foodPreference")}
              onValueChange={(foodPreference) => onChange({ foodPreference })}
            />
            {/* Several at once, and picked from the backend's allergen list
                rather than typed — a client may react to more than one thing,
                and the record carries them as a list. Each stays on as a chip
                that can be taken off again without reopening the list. */}
            <MultiSelectField
              label="Food allergies"
              id={controlId("foodAllergies")}
              {...lookupField(lookups.allergens, "food allergy", "Milk")}
              values={client.foodAllergies}
              error={errorFor("foodAllergies")}
              onBlur={visit("foodAllergies")}
              onValuesChange={(foodAllergies) => onChange({ foodAllergies })}
            />
          </div>
        </FormSection>

        {/* What the client has already bought, where they arrive having bought
            something. Optional as a whole — most clients are added before any
            money changes hands, and the Payments tab raises the request when
            it does — but required within itself: the three describe one sale,
            and the backend cannot invoice two thirds of one. */}
        {subscription && (
          <FormSection title="Subscription details (optional)">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
              <DropdownField
                label="Plan"
                id={controlId("planId")}
                {...planField}
                value={client.planId}
                error={errorFor("planId")}
                onBlur={visit("planId")}
                onValueChange={handlePlanChange}
              />
              {/* What was actually paid, which the picked plan's price
                  fills in — lower it where a discount was given, and the
                  backend records the difference as one. */}
              <TextField
                label="Paid price"
                id={controlId("paidPrice")}
                placeholder="1,500"
                prefix="₹"
                inputMode="numeric"
                value={client.paidPrice}
                error={errorFor("paidPrice")}
                onBlur={visit("paidPrice")}
                onValueChange={(paidPrice) =>
                  onChange({ paidPrice: normaliseAmountInput(paidPrice) })
                }
              />
              {/* The day the money came in, which is what dates the
                  engagement the backend opens from it — so it cannot be a day
                  that has not happened yet. */}
              <DateField
                label="Purchased date"
                id={controlId("purchasedDate")}
                value={client.purchasedDate}
                normalise={normaliseDobInput}
                maxDate={TODAY}
                error={errorFor("purchasedDate")}
                onBlur={visit("purchasedDate")}
                onValueChange={(purchasedDate) => onChange({ purchasedDate })}
              />
            </div>
          </FormSection>
        )}

        {/* Everything the pre-consultation form asks that this one did not, in
            the four groups that form asks them in — a practitioner who has
            taken a client's details over the phone can put them straight in
            here rather than sending a form for answers they already have.

            Behind a fold, and optional to the last field. Not one of these is
            required to save a client, and a form that opened showing twenty
            more boxes would read as a far longer form than it is. Nothing
            inside is validated: what is answered is sent, what is left is left
            — see `toOtherDetailFields`, which is careful not to send a blank
            over an answer the client gave on the form itself. */}
        <AccordionSection
          title="Additional client details (optional)"
          description="Add any relevant notes about the client's body, lifestyle, health, or daily routine."
          filledCount={otherAnswerCount(client)}
        >
          <div className="flex flex-col gap-6">
            <OtherGroup title="Body">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
                <MeasureField
                  id={otherId("height")}
                  label="Height"
                  placeholder="eg. 168"
                  value={client.height}
                  onValueChange={(height) => onChange({ height })}
                  unit={client.heightUnit}
                  units={HEIGHT_UNITS}
                  onUnitChange={(heightUnit) => onChange({ heightUnit })}
                />
                <MeasureField
                  id={otherId("weight")}
                  label="Weight"
                  placeholder="eg. 68"
                  value={client.weight}
                  onValueChange={(weight) => onChange({ weight })}
                  unit={client.weightUnit}
                  units={WEIGHT_UNITS}
                  onUnitChange={(weightUnit) =>
                    // The target follows the weight's unit unless it has been
                    // set apart: nobody weighs a client in kilos and sets a
                    // goal in pounds, and two units on one row is a misreading
                    // waiting to happen.
                    onChange({
                      weightUnit,
                      targetWeightUnit: client.targetWeight
                        ? client.targetWeightUnit
                        : weightUnit,
                    })
                  }
                />
                <MeasureField
                  id={otherId("targetWeight")}
                  label="Target weight"
                  placeholder="eg. 58"
                  value={client.targetWeight}
                  onValueChange={(targetWeight) => onChange({ targetWeight })}
                  unit={client.targetWeightUnit}
                  units={WEIGHT_UNITS}
                  onUnitChange={(targetWeightUnit) =>
                    onChange({ targetWeightUnit })
                  }
                />
              </div>
            </OtherGroup>

            {/* The food preference and the allergies are already asked for
                above, under Requirements, so this group is only what is left:
                the cuisine they cook in, and the dishes either way. */}
            <OtherGroup title="Food">
              <div className="grid grid-cols-1 gap-4 sm:gap-5 xl:grid-cols-3">
                {/* Several, as the label has always said and as the
                    pre-consultation form asks it. Picked from the backend's
                    own list rather than typed, because what goes up is the id
                    behind each name. */}
                <MultiSelectField
                  label="Favorite cuisines"
                  id={otherId("cuisines")}
                  {...lookupField(lookups.cuisines, "cuisine", "North Indian")}
                  values={client.cuisines}
                  onValuesChange={(cuisines) => onChange({ cuisines })}
                />
                <ChipsField
                  id={otherId("dishesLoved")}
                  label="Dishes you love"
                  placeholder="eg. Rajma chawal"
                  values={client.dishesLoved}
                  onChange={(dishesLoved) => onChange({ dishesLoved })}
                  max={MAX_DISHES}
                  note={`(Add upto ${MAX_DISHES})`}
                />
                <ChipsField
                  id={otherId("dishesAvoided")}
                  label="Dishes you avoid"
                  placeholder="eg. Bitter gourd sabzi"
                  values={client.dishesAvoided}
                  onChange={(dishesAvoided) => onChange({ dishesAvoided })}
                  max={MAX_DISHES}
                  note={`(Add upto ${MAX_DISHES})`}
                />
              </div>
            </OtherGroup>

            <OtherGroup title="Daily routine">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
                <TimeRangeField
                  id={otherId("workFrom")}
                  label="Working hours"
                  from={client.workFrom}
                  to={client.workTo}
                  onFromChange={(workFrom) => onChange({ workFrom })}
                  onToChange={(workTo) => onChange({ workTo })}
                  anyHours={client.workAnyHours}
                  onAnyHoursChange={(workAnyHours) => onChange({ workAnyHours })}
                />
                <TimeRangeField
                  id={otherId("sleepFrom")}
                  label="Sleep timings"
                  from={client.sleepFrom}
                  to={client.sleepTo}
                  onFromChange={(sleepFrom) => onChange({ sleepFrom })}
                  onToChange={(sleepTo) => onChange({ sleepTo })}
                  anyHours={client.sleepAnyHours}
                  onAnyHoursChange={(sleepAnyHours) =>
                    onChange({ sleepAnyHours })
                  }
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
                {/* The backend's own choices rather than catalogs, and not
                    creatable: these are scales rather than names, and the
                    column would refuse a fourth answer invented between two of
                    them. Each holds a constant and shows a label of its own —
                    see `HABIT_OPTIONS`. */}
                <DropdownField
                  label="Travel frequency"
                  id={otherId("travel")}
                  placeholder="eg. Occasional travel"
                  options={[...HABIT_OPTIONS.travel]}
                  value={client.travel}
                  onValueChange={(travel) => onChange({ travel })}
                />
                <DropdownField
                  label="Alcohol consumption"
                  id={otherId("alcohol")}
                  placeholder="eg. Occasionally"
                  options={[...HABIT_OPTIONS.alcohol]}
                  value={client.alcohol}
                  onValueChange={(alcohol) => onChange({ alcohol })}
                />
                <DropdownField
                  label="Smoking"
                  id={otherId("smoking")}
                  placeholder="eg. Occasionally"
                  options={[...HABIT_OPTIONS.smoking]}
                  value={client.smoking}
                  onValueChange={(smoking) => onChange({ smoking })}
                />
              </div>
            </OtherGroup>

            <OtherGroup title="Health">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
                {/* Several at once, and picked rather than typed: the block
                    this goes up in takes catalog ids, so a condition written in
                    somebody's own words is one the endpoint has no id for. */}
                <MultiSelectField
                  label="Medical conditions"
                  id={otherId("conditions")}
                  {...lookupField(
                    lookups.medicalConditions,
                    "medical condition",
                    "Thyroid"
                  )}
                  values={client.conditions}
                  onValuesChange={(conditions) => onChange({ conditions })}
                />
                <ChoiceField
                  id={otherId("onMedication")}
                  label="On any medications"
                  value={client.onMedication}
                  options={MEDICATION_CHOICES}
                  width="half"
                  onChange={(onMedication) =>
                    // "No" takes the list with it. Rows left behind under a No
                    // are a contradiction somebody would have to resolve by
                    // guessing.
                    onChange({
                      onMedication,
                      medications:
                        onMedication === "No" ? [] : client.medications,
                    })
                  }
                />
              </div>

              {/* Only once there are said to be some. Asking for the names of
                  medications nobody takes is a question with no answer. */}
              {client.onMedication === "Yes" && (
                <div className="mt-4">
                  <ChipsField
                    id={otherId("medications")}
                    label="Name and dose, if you know it"
                    placeholder="eg. Metformin 500mg"
                    values={client.medications}
                    onChange={(medications) => onChange({ medications })}
                    max={10}
                  />
                </div>
              )}
            </OtherGroup>
          </div>
        </AccordionSection>
      </div>

      {/* Run out to the shell's edges the way the design has it — hence the
          negative side margins, which undo the page's own padding, and the
          matching padding put back inside so the buttons still line up with
          the cards above them.

          `PinnedActionBar` is what keeps it on the foot of the screen now
          that the page is what scrolls: the sections pass behind it, and it
          comes to rest in place once the page is scrolled all the way down.
          The white fill and the rule here are what it wears on a desktop — on
          a phone the shared bar takes both away and blurs the sections
          passing under instead. `mt-5` then holds the rule clear of the last
          card at that resting point, and the page leaves no padding below, so
          the bar's own is the last thing on the screen.

          The tab bar is not under this on a phone — the form has taken the
          bottom edge, see `useHiddenTabBar` above — so the room it kept under
          itself is this bar's to keep now, and the same amount of it: a tab
          spends 8px under its label and the bar another 12px under that, over
          the safe-area inset. Hence 20px, matching `BrandDetailsForm`'s bar to
          the pixel — they are the same bar on two forms, and somebody who
          fills in both should not be able to tell them apart. The inset is 0px
          on a phone reporting none, which is why the 20px is not left to it:
          that is the case a bar like this lands on the home indicator in. */}
      <PinnedActionBar className="-mx-page mt-5 border-t border-line bg-white px-page pt-4 pb-[calc(env(safe-area-inset-bottom)_+_1.25rem)] md:pb-4">
        {/* Each button takes half the bar on a phone — two thumb-sized
            targets side by side — and falls back to its own width from `sm`,
            where the bar is wide enough for them to sit at the right. */}
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={onCancel}
            // Leaving mid-save would abandon a client the backend is already
            // writing, and land on a list that may or may not show them.
            disabled={saving}
            className="flex-1 sm:flex-none sm:min-w-[120px]"
          >
            Cancel
          </Button>
          {/* Off until every required field is filled — the form says whether
              it can be saved before it is pressed rather than after. It is off
              the soft way while something is missing (`onDisabledClick` rather
              than the native attribute): it greys out and reads as disabled,
              but a press still marks up what is missing, so the button that
              cannot be used is still the thing that says why. `type` follows:
              submitting is the valid form's job, and while it is invalid the
              press is the reporter's. `aria-invalid` tells a screen reader what
              the red under the fields says. A save already in flight holds it
              the plain way — a second press would be a second client, and
              there is nothing to explain about work already under way. */}
          <Button
            type={valid ? "submit" : "button"}
            size="md"
            aria-invalid={valid ? undefined : true}
            disabled={!valid || saving}
            onDisabledClick={!valid && !saving ? reportProblems : undefined}
            className="flex-1 sm:flex-none sm:min-w-[140px]"
          >
            {saving ? "Saving…" : saveLabel}
          </Button>
        </div>
      </PinnedActionBar>
    </form>
  );
};

export default AddClientForm;
